"use client";

import { GradientCircle } from "./gradient_circle";
import Image from "next/image";

interface BlogBlockProps {
    className?: string;
    value?: string;
    description?: string;
    title?: string;
    subtitle?: string;
    bodyText?: string;
    rightContent?: React.ReactNode;
    color?: string;
    hideCircle?: boolean;
    bgColor?: string;
    layout?: 'three-col' | 'two-col';
    rightImage?: string;
    hasGradient?: boolean;
    gradientFrom?: string;
    gradientTo?: string;
    hasGrainyEffect?: boolean;
    showArrow?: boolean;
}

export function BlogBlock({ 
    className = "", 
    value,
    description,
    title,
    subtitle,
    bodyText,
    rightContent,
    color = 'bg-ss-light-green',
    hideCircle = false,
    bgColor,
    layout = 'three-col',
    rightImage,
    hasGradient = false,
    gradientFrom,
    gradientTo,
    hasGrainyEffect = false,
    showArrow = false
}: BlogBlockProps) {
    // Determine the background color class
    const bgColorClass = bgColor || `bg-ss-light-${color}`;

    // Three column layout: circle, text, image
    if (layout === 'three-col') {
        return (
            <section className={`${bgColorClass} rounded-3xl pl-6 pt-6 pb-6 lg:pl-8 lg:pt-8 lg:pb-8 overflow-hidden ${className}`}>
            <div className="flex flex-col lg:flex-row items-stretch lg:items-stretch gap-0">
                {/* Left: Circle */}
                {!hideCircle && value && description && (
                    <div className="shrink-0 lg:w-1/5">
                        <GradientCircle 
                            value={value}
                            color={color}
                            hasGradient={hasGradient}
                            gradientFrom={gradientFrom}
                            gradientTo={gradientTo}
                            hasGrainyEffect={hasGrainyEffect}
                            showArrow={showArrow}
                        />
                    </div>
                )}

                {/* Middle: Text Block */}
                <div className="flex flex-col text-center lg:text-left lg:w-2/5 p-4">
                    {title && (
                        <h3 className="text-sm lg:base font-bold font-serif mb-1">
                            {title}
                        </h3>
                    )}
                    {subtitle && (
                        <h4 className="text-sm lg:text-base font-bold mb-1">
                            {subtitle}
                        </h4>
                    )}
                    {bodyText && (
                        <p className="text-xs lg:text-sm opacity-80">
                            {bodyText}
                        </p>
                    )}
                </div>

                {/* Right: Image */}
                <div className="shrink-0 lg:w-2/5 lg:ml-8 lg:-mr-8">
                        {rightImage ? (
                            rightImage.endsWith('.mp4') ? (
                                <video
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    className="w-full h-auto rounded-lg"
                                >
                                    <source src={rightImage} type="video/mp4" />
                                </video>
                            ) : (
                                <Image
                                    src={rightImage}
                                    alt={title || "Blog image"}
                                    width={400}
                                    height={300}
                                    className="w-full h-auto rounded-lg"
                                />
                            )
                        ) : rightContent ? (
                            rightContent
                        ) : (
                            <Image
                                src="/blog_img.png"
                                alt={title || "Blog image"}
                                width={400}
                                height={300}
                                className="w-full h-auto rounded-lg"
                            />
                        )}
                    </div>
                </div>
            </section>
        );
    }

    // Two column layout: text (5/12) and image (7/12)
    return (
        <section className={`${bgColorClass} rounded-3xl p-6 lg:p-8 ${className}`}>
            <div className="flex flex-col lg:flex-row items-center lg:items-center gap-4 lg:gap-6">
                {/* Left: Text Block - 5/12 width */}
                <div className="flex flex-col text-center lg:text-left lg:w-5/12">
                    {title && (
                        <h3 className="text-xl lg:text-2xl font-bold font-serif mb-1">
                            {title}
                        </h3>
                    )}
                    {subtitle && (
                        <h4 className="text-sm lg:text-base font-bold mb-1">
                            {subtitle}
                        </h4>
                    )}
                    {bodyText && (
                        <p className="text-xs lg:text-sm opacity-80">
                            {bodyText}
                        </p>
                    )}
                </div>

                {/* Right: Image - 7/12 width */}
                <div className="shrink-0 lg:w-7/12">
                    {rightImage ? (
                        rightImage.endsWith('.mp4') ? (
                            <video
                                autoPlay
                                loop
                                muted
                                playsInline
                                className="w-full h-auto rounded-lg"
                            >
                                <source src={rightImage} type="video/mp4" />
                            </video>
                        ) : (
                            <Image
                                src={rightImage}
                                alt={title || "Blog image"}
                                width={1200}
                                height={800}
                                className="w-full h-auto rounded-lg"
                            />
                        )
                    ) : rightContent ? (
                        rightContent
                    ) : (
                        <Image
                            src="/blog_img.png"
                            alt={title || "Blog image"}
                            width={1200}
                            height={800}
                            className="w-full h-auto rounded-lg"
                        />
                    )}
                </div>
            </div>
        </section>
    );
}
